import React, { useState } from 'react';
import { Upload, Sparkles, Plus, X, Check, AlertCircle, Edit2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';

const QuickImportDialog = ({ isOpen, onClose, onProductsCreated, categories }) => {
  const [inputText, setInputText] = useState('');
  const [parsedProducts, setParsedProducts] = useState([]);
  const [availableCategories, setAvailableCategories] = useState(categories || []);
  const [isParsing, setIsParsing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [step, setStep] = useState(1); // 1 = input, 2 = preview

  const handleParse = async () => {
    if (!inputText.trim()) {
      toast.error('Bitte geben Sie Text ein');
      return;
    }

    setIsParsing(true);
    try {
      const response = await axiosInstance.post('/quick-import/parse', {
        text: inputText
      });

      setParsedProducts(response.data.products || []);
      setAvailableCategories(response.data.categories_available || []);
      setStep(2);

      if (response.data.products.length === 0) {
        toast.error('Keine Produkte erkannt');
      } else {
        toast.success(`${response.data.products.length} Produkt(e) erkannt`);
      }
    } catch (error) {
      console.error('Parse error:', error);
      toast.error('Fehler beim Analysieren');
    } finally {
      setIsParsing(false);
    }
  };

  const handleCreateProducts = async () => {
    if (parsedProducts.length === 0) {
      toast.error('Keine Produkte zum Erstellen');
      return;
    }

    // Validate that all products have required fields
    const invalidProducts = parsedProducts.filter(p => !p.name || !p.price);
    if (invalidProducts.length > 0) {
      toast.error('Bitte füllen Sie Name und Preis für alle Produkte aus');
      return;
    }

    setIsCreating(true);
    try {
      const response = await axiosInstance.post('/quick-import/create-products', parsedProducts);

      if (response.data.created.length > 0) {
        toast.success(response.data.message);
        onProductsCreated?.();
        handleClose();
      }

      if (response.data.errors.length > 0) {
        toast.error(`${response.data.errors.length} Fehler beim Erstellen`);
      }
    } catch (error) {
      console.error('Create error:', error);
      toast.error('Fehler beim Erstellen der Produkte');
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    setInputText('');
    setParsedProducts([]);
    setStep(1);
    setEditingIndex(null);
    onClose();
  };

  const updateProduct = (index, field, value) => {
    setParsedProducts(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const removeProduct = (index) => {
    setParsedProducts(prev => prev.filter((_, i) => i !== index));
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.8) return 'bg-green-100 text-green-800';
    if (confidence >= 0.5) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[95vw] max-w-4xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 text-purple-500 flex-shrink-0" />
            <span className="truncate">Schnell-Import mit KI-Erkennung</span>
          </DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
              <h4 className="font-semibold text-blue-900 mb-2 text-sm sm:text-base">💡 So funktioniert's:</h4>
              <ul className="text-xs sm:text-sm text-blue-800 space-y-1">
                <li>• Eine Zeile pro Artikel eingeben</li>
                <li>• Erkennt: <strong>Name, Preis, Farbe, Kategorie</strong></li>
                <li>• Beispiel: <code className="bg-blue-100 px-1 rounded text-xs">Haargummi schwarz 1.95 €</code></li>
              </ul>
            </div>

            <div>
              <Label className="text-sm sm:text-base font-semibold">Artikeldaten eingeben:</Label>
              <Textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder={`Beispiele:\nHaargummi schwarz 1.95 €\nScrunchie rosa samt 2.50€\nOhrring gold glitzer 4,99 EUR`}
                className="mt-2 min-h-[150px] sm:min-h-[200px] font-mono text-sm"
              />
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-2">
              <Button variant="outline" onClick={handleClose} className="w-full sm:w-auto">
                Abbrechen
              </Button>
              <Button onClick={handleParse} disabled={isParsing || !inputText.trim()} className="w-full sm:w-auto">
                {isParsing ? (
                  <>
                    <Sparkles className="h-4 w-4 mr-2 animate-spin" />
                    Analysiere...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Mit KI analysieren
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div>
                <h3 className="font-semibold text-sm sm:text-base">{parsedProducts.length} Produkt(e) erkannt</h3>
                <p className="text-xs sm:text-sm text-gray-500">Prüfen und bearbeiten Sie die Daten vor dem Speichern</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => setStep(1)} className="w-full sm:w-auto">
                ← Zurück zur Eingabe
              </Button>
            </div>

            <div className="space-y-3 max-h-[50vh] sm:max-h-[400px] overflow-y-auto">
              {parsedProducts.map((product, index) => (
                <div
                  key={index}
                  className="border rounded-lg p-3 sm:p-4 bg-white hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-3 gap-2">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 min-w-0 flex-1">
                      <Badge className={`${getConfidenceColor(product.confidence)} text-xs flex-shrink-0`}>
                        {Math.round(product.confidence * 100)}% erkannt
                      </Badge>
                      <span className="text-xs text-gray-400 truncate">"{product.raw_text}"</span>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingIndex(editingIndex === index ? null : index)}
                        className="h-8 w-8 p-0"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeProduct(index)}
                        className="text-red-500 hover:text-red-700 h-8 w-8 p-0"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Always show editable form on mobile, or when editing */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <Label className="text-xs">Name *</Label>
                      <Input
                        value={product.name || ''}
                        onChange={(e) => updateProduct(index, 'name', e.target.value)}
                        className="mt-1 h-9 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Preis (€) *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={product.price || ''}
                        onChange={(e) => updateProduct(index, 'price', parseFloat(e.target.value) || 0)}
                        className="mt-1 h-9 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Kategorie</Label>
                      <select
                        value={product.category || ''}
                        onChange={(e) => updateProduct(index, 'category', e.target.value)}
                        className="mt-1 w-full h-9 px-2 rounded-md border border-gray-300 text-sm"
                      >
                        <option value="">-- Auswählen --</option>
                        {availableCategories.map((cat) => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs">Lagerbestand</Label>
                      <Input
                        type="number"
                        value={product.stock || 10}
                        onChange={(e) => updateProduct(index, 'stock', parseInt(e.target.value) || 0)}
                        className="mt-1 h-9 text-sm"
                      />
                    </div>
                    <div className="col-span-1 sm:col-span-2 md:col-span-4">
                      <Label className="text-xs">Beschreibung</Label>
                      <Input
                        value={product.description || ''}
                        onChange={(e) => updateProduct(index, 'description', e.target.value)}
                        className="mt-1 h-9 text-sm"
                        placeholder="Optionale Beschreibung..."
                      />
                    </div>
                  </div>

                  {/* Validation warnings */}
                  {(!product.name || !product.price) && (
                    <div className="mt-2 flex items-center gap-1 text-orange-600 text-xs">
                      <AlertCircle className="h-3 w-3" />
                      {!product.name && 'Name fehlt'} {!product.price && 'Preis fehlt'}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row justify-between items-center gap-3 pt-4 border-t">
              <p className="text-xs sm:text-sm text-gray-500">
                {parsedProducts.filter(p => p.name && p.price).length} von {parsedProducts.length} Produkten bereit
              </p>
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                <Button variant="outline" onClick={handleClose} className="w-full sm:w-auto">
                  Abbrechen
                </Button>
                <Button
                  onClick={handleCreateProducts}
                  disabled={isCreating || parsedProducts.filter(p => p.name && p.price).length === 0}
                  className="w-full sm:w-auto"
                >
                  {isCreating ? (
                    <>
                      <Sparkles className="h-4 w-4 mr-2 animate-spin" />
                      Erstelle...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      {parsedProducts.length} Produkt(e) erstellen
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default QuickImportDialog;
